/**
 * Auth service — manages authentication state for the SPA.
 * Communicates with the BFF auth routes (server handles tokens).
 */
import { authService, ApiError } from './api';

export interface AppUser {
  _id: string;
  okta_uid: string;
  email: string;
  display_name: string;
  platform_role: 'admin' | 'user';
  last_login: string;
}

let _currentUser: AppUser | null = null;

/** Check if the user is authenticated. Returns user or null. */
export async function checkAuth(): Promise<AppUser | null> {
  try {
    const { user } = await authService.me();
    _currentUser = user;
    return user;
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 401) {
      _currentUser = null;
      return null;
    }
    throw err;
  }
}

/** Get the cached current user (call checkAuth first). */
export function getCurrentUser(): AppUser | null {
  return _currentUser;
}

/** Is the current user a platform admin? */
export function isPlatformAdmin(): boolean {
  return _currentUser?.platform_role === 'admin';
}

/** Redirect to Okta login, preserving the current URL as return destination. */
export function login(): void {
  const returnTo = window.location.pathname + window.location.search + window.location.hash;
  const url = returnTo && returnTo !== '/'
    ? `${authService.loginUrl}?returnTo=${encodeURIComponent(returnTo)}`
    : authService.loginUrl;
  window.location.href = url;
}

/** Logout and clear session. */
export async function logout(): Promise<void> {
  await authService.logout();
  _currentUser = null;
  window.location.href = '/';
}

/** Check if Okta is configured. */
export async function checkOktaStatus(): Promise<{ configured: boolean; setupRequired: boolean }> {
  const status = await authService.status();
  return {
    configured: status.okta_configured,
    setupRequired: status.setup_required,
  };
}
